import { UserMarkup_addNote, UserMarkup_viewNote } from './UserMarkupPopup.locstring.json';
import addUserNote from '../actions/addUserNote';
import getSelectionInstance from '../utils/getSelectionInstance';
import { getStickyNoteId } from '../utils/getValidStickyNote';
import { IconButton } from '@fluentui/react/lib/Button';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { IPoint } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import { ControlIcons } from 'owa-control-icons';
import { convertEwsIdToRestId } from 'owa-identifiers';
import loc from 'owa-localize';
import { MailIcons } from 'owa-mail-icons';
import { ClientItem } from 'owa-mail-store';
import { openOneNoteFeedPanel } from 'owa-notes-feed-bootstrap';
import { lazyCreateNoteFromMessageData, lazyGetNotesSDK } from 'owa-notes-store';
import { trace } from 'owa-trace';
import getMailItemReadDeepLinkUrl from 'owa-url/lib/getMailItemReadDeepLinkUrl';
import React from 'react';
import { logUsage } from 'owa-analytics';

const styles = require('./UserMarkupPopup.scss');

export interface NoteOptionProps {
    target: IPoint | HTMLElement;
    selectedText: string;
    element: HTMLElement;
    item: ClientItem;
    isForExistingUserNote: boolean;
    selectionRange?: Range;
    selectionInstance?: number;
    destroyPopupDivCallback: () => void;
}

const NoteOption = observer(function NoteOption(props: NoteOptionProps) {
    const {
        target,
        selectedText,
        element,
        item,
        isForExistingUserNote,
        selectionRange,
        selectionInstance,
        destroyPopupDivCallback,
    } = props;

    const onUserNoteSaved = React.useCallback(
        async (remoteId: string) => {
            const noteSaved = await tryOpenNoteUsingRemoteId(remoteId);

            if (noteSaved) {
                // Add the user note so it is preserved upon the user reentering the message
                addUserNote(
                    element,
                    item,
                    selectedText,
                    isForExistingUserNote
                        ? selectionInstance
                        : getSelectionInstance(selectedText, element, selectionRange),
                    isForExistingUserNote,
                    remoteId
                );

                // Log a datapoint to track that the user saved a note.
                logUsage('NoteCreatedFromRP', {
                    isCore: true,
                    excludeFromKusto: true, // only log to COSMOS
                });
            }
        },
        [element, item, selectedText, selectionInstance, isForExistingUserNote]
    );

    const onShowNote = React.useCallback(() => {
        destroyPopupDivCallback();

        openOneNoteFeedPanel('Honeybee');

        if (!isForExistingUserNote) {
            // If there is no existing note, create a new one.
            const webLinkId = convertEwsIdToRestId(item.ItemId.Id);
            const webLink = getMailItemReadDeepLinkUrl(item.ItemId.Id);
            lazyCreateNoteFromMessageData.importAndExecute(
                selectedText,
                webLinkId,
                webLink,
                item.Subject,
                'NotesFeedFlexPane',
                onUserNoteSaved
            );
        } else {
            // We are opening an existing note.
            // Get the remoteId from the item and then open it in the SDK.
            const remoteId = getStickyNoteId(selectedText, selectionInstance, item);
            tryOpenNoteUsingRemoteId(remoteId);
        }
    }, [
        target,
        selectedText,
        element,
        item,
        isForExistingUserNote,
        selectionRange,
        selectionInstance,
        destroyPopupDivCallback,
    ]);

    return (
        <TooltipHost
            content={isForExistingUserNote ? loc(UserMarkup_viewNote) : loc(UserMarkup_addNote)}>
            <IconButton
                className={styles.UserMarkupPopupButton}
                iconProps={
                    isForExistingUserNote
                        ? { iconName: ControlIcons.View }
                        : { iconName: MailIcons.EditNote }
                }
                onClick={onShowNote}
            />
        </TooltipHost>
    );
});

async function tryOpenNoteUsingRemoteId(remoteId: string) {
    const getNotesSDK = await lazyGetNotesSDK.import();
    const layoutSDK = getNotesSDK('NotesFeedFlexPane');
    await layoutSDK._ready;

    const currentNote = await layoutSDK.notesSDK?.getNote(remoteId);

    if (currentNote) {
        if (!layoutSDK.openNote(currentNote.id)) {
            trace.warn(`[NoteOption] Failed to open note using note ID: ${currentNote.id}`);
            return false;
        } else {
            return true;
        }
    } else {
        // Could not open note
        trace.warn(`[NoteOption] Failed to get note using note ID: ${currentNote.id}`);
        return false;
    }
}

export default NoteOption;
